import { useCallback, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import axios from "axios";
import {
  setAuthTokenResponseData, setLogoutSession,
} from "../slices/authTokenSlice";
import { get, post, remove } from "../../utils/common-hooks/fetchData";
import { ApiEndpoints } from "../../utils/constants/endpointConstants";
import { setAirPorts } from "../slices/airPortsSlice";
import { setGetAirLine } from "../slices/getAirlinesSlice";
import { setSourceData } from "../slices/sourceDataSlice";
import { setMarketGroup } from "../slices/marketGroupSlice";
import { setuserAirline } from "../slices/userAirlineSlice";
import { setFetchAirline } from "../slices/fetchAirlineSlice";
import { setTimeZoneData } from "../slices/timeZoneSlice";
import { setSnackBarData } from "../slices/snackBarSlice";
import { setScoreCardData, setTodayScoreCardData } from "../slices/scoreCard";
import { setUserDetailsData } from "../slices/userDetails";
import { setDashboardData } from "../slices/dashboardDataSlice";
import { setDownloading, setMarketLoading, setMyMarket, setMyMarketForGroup, setMyMarketResponse, setVisCount } from "../slices/myMarketSlice";
import { setTableauLibraryItems } from "../slices/tableauLibraryItems";
import { isValue } from "../../utils/constants/constants";
import { clearAllReduxStates } from "../../utils/common-hooks/clearState";
import { persistor } from "../../store/store";
import { responseData } from "../../utils/helpers/helper";

// One active shop of the account, with no filter: its answer carries visCount (see fetchVisualizedCount)
const VISUALIZED_COUNT_PAYLOAD = {
  shopName: "", flyFrom: "", flyTo: "", frequency: [], days: [], status: 1, cabinClass: 0, page: 1, pageSize: 1,
  routeType: 0, pos: "", currency: "", maxStops: "", maxDuration: "", sources: "",
  visualizeOnly: false, nonvisualizeOnly: false, groupShops: [],
};

const APIServices = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { authTokenResponseData } = useSelector(state => state?.authToken);
  const { downloading } = useSelector((state) => state?.MyMarket);
  const { timezone } = useSelector(state => state?.timezone);
  const { airPorts } = useSelector(state => (state?.airports));
  const { getAirLine } = useSelector(state => (state?.getAirlines));
  const { userAirline } = useSelector(state => state?.userAirline);
  const { userdetails } = useSelector(state => state?.userDetails);
  const isAuthenticated = Boolean(authTokenResponseData?.isAuthenticated);
  // Positional argument kept for get/post/remove; the session itself is an httpOnly cookie
  const token = null;
  // fetchData resolves a failed request to the API's message (a string); show it so a failed action isn't silent
  const notifyFailure = (result) => {
    if (typeof result === "string" && result) dispatch(setSnackBarData(result));
  };

  // Define getData outside of getStaticDatas to prevent it from being recreated on each render
  const getData = useCallback(async (token, controller, setLoading, setError,) => {
    if (!controller.current) {
      const timezonePromise = get(ApiEndpoints.get.timezoneEndpoint, controller, setLoading, setError, token, navigate, dispatch);
      const userAirlinePromise = get(ApiEndpoints.get.userAirlineEndpoint, controller, setLoading, setError, token, navigate, dispatch);
      const airPortsPromise = get(ApiEndpoints.get.airPortsEndpoint, controller, setLoading, setError, token, navigate, dispatch);
      const getAirlinesPromise = get(ApiEndpoints.get.getAirlinesEndpoint, controller, setLoading, setError, token, navigate, dispatch);
      const sourcePromise = get("sources", controller, setLoading, setError, token, navigate, dispatch);
      const userDetailsPromise = get(ApiEndpoints?.get?.userdetails, controller, setLoading, setError, token, navigate, dispatch);
      const getMarketGroupPromises = post("getmarketgroup", controller, {
        "groupName": isValue?.emptyString,
        "flyFrom": isValue?.emptyString,
        "flyTo": isValue?.emptyString,
        "fsIds": []
      }, setLoading, setError, token, navigate, dispatch);
      const [timezoneApi, userAirlineApi, airPortsApi, getAirlinesApi, getMarketGroup, sourceData, userDetails] = await Promise.all([timezonePromise, userAirlinePromise, airPortsPromise, getAirlinesPromise, getMarketGroupPromises, sourcePromise, userDetailsPromise]);
      return { timezoneApi, userAirlineApi, airPortsApi, getAirlinesApi, getMarketGroup, sourceData, userDetails };
    }
  }, [dispatch, navigate]);

  // My Account Data 
  const accountData = useCallback(async (controller, setLoading, setError) => {
    const getAccountPromise = await get(ApiEndpoints.get.getAccount, controller, setLoading, setError, token, navigate, dispatch);
    return getAccountPromise
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  const getStaticDatas = useCallback(async (controller, setLoading, setError) => {

    try {
      if (!isAuthenticated) {
        setError("Not signed in")
        return;
      }
      if (!Object.keys(timezone).length ||
        !Object.keys(airPorts).length ||
        !Object.keys(getAirLine).length ||
        !Object.keys(userAirline).length ||
        !Object.keys(userdetails).length)
        await getData(token, controller, setLoading, setError)
          .then(data => {
            if (data?.userDetails?.status) {
              dispatch(setUserDetailsData(data?.userDetails?.data))
            }
            if (data?.timezoneApi?.status) {
              dispatch(setTimeZoneData(data?.timezoneApi?.data));
            }
            if (data?.userAirlineApi?.status) {
              dispatch(setuserAirline(data?.userAirlineApi?.data));
            }
            if (data?.getAirlinesApi?.status) {
              dispatch(setGetAirLine(data?.getAirlinesApi?.data));
            }
            if (data?.airPortsApi?.status) {
              dispatch(setAirPorts(data?.airPortsApi?.data));
            }
            if (data?.sourceData?.status) {
              dispatch(setSourceData(data?.sourceData?.data));
            }
            if (data?.getMarketGroup?.status) {
              dispatch(setMarketGroup(data?.getMarketGroup?.data));
            }
          });
    } catch (error) {
      setError("Error fetching static data");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAuthenticated, timezone, airPorts, getAirLine, userAirline, getData, dispatch]);

  // Login To Fetching Token
  const fetchAuthToken = useCallback(async (data, setLoading, setError, controller) => {
    const payload = {
      ...data,
      ...{ grantType: "password" },
    };
    const response = await post(ApiEndpoints?.post?.auth, controller, payload, setLoading, setError, null, navigate, dispatch);
    if (!response) return
    if (response?.status) {
      // The API set the session cookies; Redux only records that we're signed in
      dispatch(setAuthTokenResponseData({ ...response?.data, isAuthenticated: true }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch]);

  // Login To Fetching Token
  const ipLogUpdate = useCallback(async (data, setLoading = () => { }, setError = () => { }, controller) => {
    const response = await get(ApiEndpoints?.post?.ipUpdate, controller, setLoading, setError, token, navigate, dispatch)
    if (!response) return
    if (response?.status) {
      return response
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch]);

  // `force` skips the "already loaded" check: a new impersonated login must never reuse the previous user's details
  const fetchUserDetails = useCallback(async (id, controller, setLoading, setError, userToken, force = false) => {
    if (force || !Object?.keys(userdetails)?.length) {
      const getUserDetailsPromise = await get(ApiEndpoints?.get?.userdetails, controller, setLoading, setError, userToken || token, navigate, dispatch);
      if (getUserDetailsPromise?.status) {
        dispatch(setUserDetailsData(getUserDetailsPromise?.data));
        if (getUserDetailsPromise?.data?.isVisualize) {
          if (getUserDetailsPromise?.data?.isDashboard === true && !id) {
            navigate(`/quick_access${authTokenResponseData?.userName ? `/?token=${authTokenResponseData?.userName}` : ''}`)
          } else if (id) {
            navigate(`/view_dashboard`, {
              state: {
                fsId: id,
                from: "bdemagiclink"
              }
            })
          } else if (!id) {
            navigate(`/shop_manager${authTokenResponseData?.userName ? `/?jwt=${authTokenResponseData?.userName}` : ''}`)
          }
        }
      }
    } else if (Object?.keys(userdetails)?.length && id) {
      const getUserDetailsPromise = await get(ApiEndpoints?.get?.userdetails, controller, setLoading, setError, userToken || token, navigate, dispatch);
      if (getUserDetailsPromise?.status) {
        dispatch(setUserDetailsData(getUserDetailsPromise?.data));
        if (getUserDetailsPromise?.data?.isVisualize) {
          if (id) {
            navigate(`/view_dashboard`, {
              state: {
                fsId: id,
                from: "bdemagiclink"
              }
            })
          }
        }
      }
    }
    /*eslint-disable-next-line*/
  }, [dispatch, token, userdetails, navigate]);

  // fetch My market data
  const fetchMyMarket = useCallback(async (myMarketPayload, controller, setLoading, setError, loading, isRetry = false) => {
    if (!loading) {
      if (!isRetry) dispatch(setMarketLoading(true))
      const myMarketResponse = await post(ApiEndpoints?.post?.mymarketpage, controller, myMarketPayload, setLoading, setError, token, navigate, dispatch);
      if (myMarketResponse?.status) {
        dispatch(setMyMarketResponse(myMarketResponse))
        dispatch(setMyMarket(myMarketResponse?.data?.message !== "Markets Not available" ? myMarketResponse?.data : { data: [] }));
        if (!isRetry) dispatch(setMarketLoading(false))
        dispatch(setDownloading(myMarketResponse?.data?.data?.length > 0));
        return myMarketResponse?.data
      } else {
        if (!isRetry) dispatch(setMarketLoading(false))
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch, token, downloading]);

  // The account's active visualized-shop count only (the visCount of /mymarketpage, which ignores the
  // filter). Asks for one active shop and stores nothing but the count, so the My Market list, its
  // filter, paging and loading state are untouched. Returns the count, or undefined when unknown.
  const fetchVisualizedCount = useCallback(async (controller) => {
    const payload = { ...VISUALIZED_COUNT_PAYLOAD }
    const response = await post(ApiEndpoints?.post?.mymarketpage, controller, payload, () => { }, () => { }, token, navigate, dispatch);
    if (!response?.status) return undefined
    const count = response?.data?.visCount ?? (response?.data?.message === "Markets Not available" ? 0 : undefined)
    if (typeof count === "number") dispatch(setVisCount(count))
    return count
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch, token]);

  //fetch My market data for group table
  const fetchMyMarketforgroup = useCallback(async (myMarketPayload, controller, setLoading, setError, loading, isRetry = false) => {
    if (!loading) {
      if (!isRetry) dispatch(setMarketLoading(true))
      const myMarketResponse = await post(ApiEndpoints?.post?.mymarketpage, controller, myMarketPayload, setLoading, setError, token, navigate, dispatch);
      if (myMarketResponse?.status) {
        const data = myMarketResponse?.data?.message !== "Markets Not available" ? myMarketResponse?.data : { data: [] };
        dispatch(setMyMarketForGroup(data));
        if (!isRetry) dispatch(setMarketLoading(false));
        return data;
      } else {
        if (!isRetry) dispatch(setMarketLoading(false));
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch, token, downloading]);

  // Delete My Market Data
  const deleteMyMarket = useCallback(async (value, controller, setLoading, setError) => {
    const myMarketResponse = await remove(`${ApiEndpoints?.remove?.market}/${value}`, controller, setLoading, setError, token, navigate, dispatch);
    if (myMarketResponse?.status) {
      dispatch(setSnackBarData(myMarketResponse.data.message));
      return (
        myMarketResponse.data
      )
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch, token]);

  // Delete My Market Data

  const deleteBulkMyMarket = useCallback(async (myMarketPayload, controller, setLoading, setError, loading) => {
    if (!loading) {
      const myBulkMarketResponse = await post(ApiEndpoints?.post?.bulkMarketDelete, controller, myMarketPayload, setLoading, setError, token, navigate, dispatch);
      if (myBulkMarketResponse?.status) {
        dispatch(setSnackBarData(myBulkMarketResponse.data.message));
        setLoading(false)
        return (
          myBulkMarketResponse.data
        )
      }
      notifyFailure(myBulkMarketResponse)
      setLoading(false)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch, token, downloading]);

  // fetch market group data
  const fetchMarketGroup = useCallback(async (groupMarketPayload, controller, setLoading, setError) => {
    const MarketGroup = await post(ApiEndpoints?.post?.getmarketgroup, controller, groupMarketPayload, setLoading, setError, token, navigate, dispatch)
    if (MarketGroup?.status) {
      dispatch(setMarketGroup(MarketGroup?.data));
      return MarketGroup?.data
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch, token]);

  // BDE "login as", magic and share links carry a one-time code minted by the BDE Panel API.
  // Redeeming it sets the session cookies for the impersonated account.
  const loginWithLink = useCallback(async (loginCode, controller, setLoading, setError) => {
    setLoading(true)
    const response = await post(ApiEndpoints?.post?.codeExchange, controller, { loginCode }, setLoading, setError, token, navigate, dispatch)
    setLoading(false)
    return response
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch, token]);

  // Delete My Market Data
  const deleteGroupShop = useCallback(async (value, controller, setLoading, setError) => {
    const MarketGroup = await remove(`${ApiEndpoints?.groupdelete?.market}?group=${value}`, controller, setLoading, setError, token, navigate, dispatch);
    if (MarketGroup?.status) {
      dispatch(setSnackBarData(MarketGroup.data.message))
      return (
        MarketGroup.data
      )
    }
    notifyFailure(MarketGroup)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch, token]);

  // Excel Data 
  const fetchExcelData = useCallback(async (excelPayload, controller, setLoading, setError) => {
    // callpy
    const downloadShopData = await post("callpyBulk", controller, excelPayload, setLoading, setError, token, navigate, dispatch);
    if (downloadShopData?.status) {
      return (downloadShopData?.data?.message === "Ok")
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  const fetchAirlineData = useCallback(async (airplinesPayload, controller, setLoading, setError) => {
    const fetchAirline = await post(ApiEndpoints?.post?.fetchAirline, controller, airplinesPayload, setLoading, setError, token, navigate, dispatch);
    if (fetchAirline?.status) {
      dispatch(setFetchAirline(fetchAirline?.data));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token, dispatch]);

  const addMarketGroup = useCallback(async (controller, addgroupayload, setLoading, setError) => {
    const addMarketGroupresponse = await post("addmarketgroup", controller, addgroupayload, setLoading, setError, token, navigate, dispatch);
    if (addMarketGroupresponse?.status) {
      return (addMarketGroupresponse);
    } else if (addMarketGroupresponse === "Groupname Already Exists.!") {
      return ({ message: "Groupname Already Exists.!" })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  const editMarketGroup = useCallback(async (id, payload, setLoading) => {
    try {
      setLoading(true)
      const resp = await axios.put(
        `${process.env.REACT_APP_API_URL}${`editmarketgroup/${id}`}`,
        payload,
        {
          headers: {
          }
        }
      );
      const res = responseData(resp)
      if (res.message) {
        setLoading(false)
        return res?.message
      }
    } catch (error) {
      console.log("API failed", error?.response?.status, error?.message)
      setLoading(false)
      if (error?.response?.status === 401 && responseData(error?.response)?.message === "New login found.") {
        dispatch(setLogoutSession(true));
      } else if (error?.response?.status === 401) {
        clearAllReduxStates(dispatch)
        persistor.purge();
        sessionStorage.clear();
        localStorage.clear();
        navigate("/")
      } else if (error?.response?.status === 403 && responseData(error?.response)?.message) {
        return responseData(error?.response)?.message
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  const addGroupToDashboard = useCallback(async (id, payload) => {
    try {
      const resp = await axios.put(
        `${process.env.REACT_APP_API_URL}${`addgroupdashboard/${id}`}`,
        payload,
        {
          headers: {
          }
        }
      );
      const res = await responseData(resp)
      if (res.message) {
        return res?.message
      }
    } catch (error) {
      console.log("API failed", error?.response?.status, error?.message)
      if (error?.response?.status === 401 && responseData(error?.response)?.message === "New login found.") {
        dispatch(setLogoutSession(true));
      } else if (error?.response?.status === 401) {
        clearAllReduxStates(dispatch)
        persistor.purge();
        sessionStorage.clear();
        localStorage.clear();
        navigate("/")
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  const addVizualise = useCallback(async (controller, addVizpayload, setLoading, setError) => {

    const addviz = await post("visualizetable", controller, addVizpayload, setLoading, setError, token, navigate, dispatch);
    if (addviz?.status) {
      return addviz
    }
    // Refused (e.g. the visualized-shop limit): show the API's message
    notifyFailure(addviz)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token])

  const fetchTabluaeData = useCallback(async (urlType, controller, setLoading, setError) => {
    const tabluae = await get(`logintableau/${urlType}`, controller, setLoading, setError, token, navigate, dispatch)
    if (tabluae?.status) {
      return (tabluae?.data?.accessUrl)
    } else {
      return "contain error"
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  const fetchVisualizeInfo = useCallback(async (urlType, controller, setLoading, setError) => {
    await get(urlType, controller, setLoading, setError, token, navigate, dispatch)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  const fetchCloneInfo = useCallback(async (urlType, controller, setLoading, setError) => {
    await get(urlType, controller, setLoading, setError, token, navigate, dispatch)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  const fetchNotificationInfo = useCallback(async (urlType, controller, setLoading, setError) => {
    await get(urlType, controller, setLoading, setError, token, navigate, dispatch)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  const passwordChange = useCallback(async (controller, passwordPayload, setLoading, setError) => {
    const changePassword = await post("forget_user", controller, passwordPayload, setLoading, setError, token, navigate, dispatch);
    if (changePassword?.status) {
      return (changePassword?.data)
    } else {
      return "contain error"
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  // scorecards
  const scorecard = useCallback(async (controller, scorecardPayload, setLoading, currentTab, setError) => {
    // dispatch(setMarketLoading(true))
    const scorecardres = await post("scorecards", controller, scorecardPayload, setLoading, setError, token, navigate, dispatch)
    if (scorecardres?.status) {
      dispatch(setScoreCardData(scorecardres?.data))
      if (parseInt(currentTab) === 4) {
        dispatch(setTodayScoreCardData(scorecardres?.data))
      }
      return scorecardres?.status
    } else {
      return "contain error"
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch, token]);

  const getLogs = useCallback(async (id, controller, setLoading, setError) => {
    const getLogsData = await get(`logs/${id}`, controller, setLoading, setError, token, navigate, dispatch)
    if (getLogsData?.status) {
      return getLogsData?.data
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token])

  const onHold = useCallback(async (id) => {
    try {
      const resp = await axios.put(`${process.env.REACT_APP_API_URL}${`market/onhold/${id}`}`)
      const res = await (resp)
      if (res.data) {
        if (res?.data?.message === "Flightshop onholded!") {
          dispatch(setSnackBarData("Flightshop Paused!"))
        } else {
          dispatch(setSnackBarData(res?.data?.message))
        }
        return res?.data?.message
      }
    } catch (error) {
      if (error?.response?.status === 401 && responseData(error?.response)?.message === "New login found.") {
        dispatch(setLogoutSession(true));
      } else if (error?.response?.status === 401) {
        clearAllReduxStates(dispatch)
        persistor.purge();
        sessionStorage.clear();
        localStorage.clear();
        navigate("/")
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch, token]);

  const onHoldBulkMyMarket = useCallback(async (Payload, controller, setLoading, setError, loading) => {
    if (!loading) {
      const myBulkMarketResponse = await post(ApiEndpoints?.post?.bulkMarketOnHold, controller, Payload, setLoading, setError, token, navigate, dispatch);
      if (myBulkMarketResponse?.status) {
        dispatch(setSnackBarData(myBulkMarketResponse.data.message));
        return (
          myBulkMarketResponse.data?.message
        )
      }
      notifyFailure(myBulkMarketResponse)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch, token, downloading]);

  const onResume = useCallback(async (id) => {
    try {
      const resp = await axios.put(`${process.env.REACT_APP_API_URL}market/resume/${id}`);
      const res = await responseData(resp)
      const message = res?.message || res?.data?.message;
      if (message) {
        dispatch(setSnackBarData(message))
        return message
      }
    } catch (error) {
      // Refused (e.g. the visualized-shop limit): show the API's message, nothing was resumed
      const refused = error?.response?.status === 400 && responseData(error?.response)?.message
      if (refused) dispatch(setSnackBarData(refused))
      if (error?.response?.status === 401 && responseData(error?.response)?.message === "New login found.") {
        dispatch(setLogoutSession(true));
      } else if (error?.response?.status === 401) {
        clearAllReduxStates(dispatch)
        persistor.purge();
        sessionStorage.clear();
        localStorage.clear();
        navigate("/")
      }
    }
    //eslint-disable-next-line
  }, [dispatch, token]);

  const onResumeBulkMyMarket = useCallback(async (Payload, controller, setLoading, setError, loading) => {
    if (!loading) {
      const myBulkMarketResponse = await post(ApiEndpoints?.post?.bulkMarketResume, controller, Payload, setLoading, setError, token, navigate, dispatch);
      if (myBulkMarketResponse?.status) {
        dispatch(setSnackBarData(myBulkMarketResponse.data.message));
        return (
          myBulkMarketResponse.data?.message
        )
      }
      notifyFailure(myBulkMarketResponse)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch, token, downloading]);

  const conFirmInGroup = useCallback(async (id, controller, setLoading, setError) => {
    const confirmResponse = await get(`ingroup/${id}`, controller, setLoading, setError, token, navigate, dispatch);
    if (confirmResponse?.status) {
      return confirmResponse?.data?.message
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token])

  const bulkConFirmInGroup = useCallback(async (ids, controller, setLoading, setError) => {
    const checkInGroup = await get(`market/shopsingroup?shops=${ids}`, controller, setLoading, setError, token, navigate, dispatch)
    if (checkInGroup?.status) {
      return checkInGroup?.data
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token])

  const getMarketGroup = useCallback(async (controller, setLoading, setError,) => {
    const getMarketGroupPromises = await post("getmarketgroup", controller, {
      "groupName": isValue?.emptyString,
      "flyFrom": isValue?.emptyString,
      "flyTo": isValue?.emptyString,
      "fsIds": []
    }, setLoading, setError, token, navigate, dispatch)
    if (getMarketGroupPromises?.status) {
      dispatch(setMarketGroup(getMarketGroupPromises?.data))
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch, token])

  const groupDataDetails = useCallback(async (controller, payload, setLoading, setError,) => {
    const getGroupDataDetails = await post("marketdetails", controller, payload, setLoading, setError, token, navigate, dispatch);
    if (getGroupDataDetails?.status) {
      return getGroupDataDetails?.data
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  const globalDownload = useCallback(async (controller, payload, setLoading, setError,) => {
    const globalDownloadDetails = await post("globaldownload", controller, payload, setLoading, setError, token, navigate, dispatch);
    if (globalDownloadDetails?.status) {
      return globalDownloadDetails?.data
    } else {
      return globalDownloadDetails
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  const shopDownload = useCallback(async (controller, payload, setLoading, setError,) => {
    const shopDownloadDetails = await post("shopdownload", controller, payload, setLoading, setError, token, navigate, dispatch);
    if (shopDownloadDetails?.status) {
      return shopDownloadDetails?.data
    } else {
      return shopDownloadDetails
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  const userAirlineData = useCallback(async (controller, setLoading, setError) => {
    const res = await get(ApiEndpoints.get.userAirlineEndpoint, controller, setLoading, setError, token, navigate, dispatch);
    if (res?.status) {
      return dispatch(setuserAirline(res?.data));

    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch, token])

  const getOverviewStats = useCallback(async (controller, setLoading, setError) => {
    const res = await get(ApiEndpoints.get.getoverviewstats, controller, setLoading, setError, token, navigate, dispatch);
    if (res?.status) {
      return res?.data;
    }
    return null;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token, navigate, dispatch])


  const getAllDashboard = useCallback(async (controller, payload, setLoading, setError,) => {
    const allDashboardDetails = await post("getdashboard", controller, payload, setLoading, setError, token, navigate, dispatch);
    if (allDashboardDetails?.status) {
      return dispatch(setDashboardData(allDashboardDetails?.data))
    } else {
      return "globaldownload_error"
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch, token]);

  const getLegacyDashboard = useCallback(async (controller, id, setLoading, setError,) => {
    const legacyDashboard = await get(`getlegacydashboard/${id}`, controller, setLoading, setError, token, navigate, dispatch);
    if (legacyDashboard?.status) {
      return legacyDashboard?.data
    } else {
      return "legacyurl_error"
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch, token]);

  const getOverAllTableau = useCallback(async (controller, params, id, setLoading, setError,) => {
    const getTableauURL = await get(`dashboardcharts/${id}`, controller, setLoading, setError, token, navigate, dispatch, params);
    if (getTableauURL?.status) {
      return dispatch(setTableauLibraryItems(getTableauURL?.data))
    } else {
      return getTableauURL === "User does not have viewer license!" ? getTableauURL : "tableau_URL_error";
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch, token]);

  const deleteDashboards = useCallback(async (value, controller, setLoading, setError) => {
    const deleteDashboardResponse = await remove(`deletedashboard?dashboard=${value}`, controller, setLoading, setError, token, navigate, dispatch);
    if (deleteDashboardResponse?.status) {
      dispatch(setSnackBarData(deleteDashboardResponse?.data?.message));
      return (
        deleteDashboardResponse.data
      )
    }
    // fetchData resolves a failure to the API's message; hand it back so the page can show why
    if (typeof deleteDashboardResponse === "string" && deleteDashboardResponse) {
      return { error: deleteDashboardResponse }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch, token]);

  const addDashboard = useCallback(async (payload, controller, setLoading, setError) => {
    const addDashboardResponse = await post("adddashboard", controller, payload, setLoading, setError, token, navigate, dispatch)
    if (addDashboardResponse?.status) {
      return addDashboardResponse
    } else {

    }
  }, [dispatch, navigate, token]);
  const editDashboard = useCallback(async (id, data, setAddDashboardLoading, setError) => {
    setAddDashboardLoading(true);

    // Create a new FormData object
    const formData = new FormData();
    // Append JSON data as a single field
    formData.append('payload', JSON.stringify({
      dashboardId: data.dashboardId,
      dashboardName: data.dashboardName,
      subTitle: data.subtitle,
      allShops: data?.allShops || false,
      historicalData: data.historicalData,
      historicalHorizon: data.historicalHorizon,
      rateparityData: data.rateparityData,
      charts: data.charts,
      groups: data.groups,
      metaSources: data.metaSources,
      otaSources: data.otaSources,
      description: data.description,
      authorizedUsers: data.authorizedUsers
    }));

    // Append the file
    if (data.dashboardImage) {
      formData.append('dashboardImage', data.dashboardImage);
    }

    const config = {
      headers: {
        // 'Content-Type': 'multipart/form-data',
      }
    };

    try {
      const responses = await axios.put(`${process.env.REACT_APP_API_URL}${`editdashboard/${id}`}`, formData, config);
      const response = await responseData(responses)
      return { status: response.data === "OK", data: response.data };
    } catch (error) {
      const status = error?.response?.status;
      const message = responseData(error?.response)?.message;
      // Client errors (validation, duplicate name, view-only, rate limit) are returned with the API's reason;
      // 401 is left to the session handling below
      if (status >= 400 && status < 500 && status !== 401) {
        return { error: message || "The dashboard could not be updated." };
      }
      setError(message || error?.message || "The dashboard could not be updated.");
      if (error?.response?.status === 401 && responseData(error?.response)?.message === "New login found.") {
        dispatch(setLogoutSession(true));
      } else if (error?.response?.status === 401) {
        clearAllReduxStates(dispatch);
        persistor.purge();
        sessionStorage.clear();
        localStorage.clear();
        navigate("/");
      }
    } finally {
      setAddDashboardLoading(false);
    }
  }, [token, dispatch, navigate]);

  const getshopsinGroup = useCallback(async (data) => {
    const config = {
      headers: {
        'Content-Type': 'application/json', // Set content type to JSON
      }
    }

    try {
      const responses = await axios.post(`${process.env.REACT_APP_API_URL}${'getAllshopingroup'}`, data, config);
      const response = await responseData(responses)
      return { status: response === "OK", data: response };
    } catch (error) {
      if (error?.response?.status === 401 && responseData(error?.response)?.message === "New login found.") {
        dispatch(setLogoutSession(true));
      } else if (error?.response?.status === 401) {
        clearAllReduxStates(dispatch)
        persistor.purge();
        sessionStorage.clear();
        localStorage.clear();
        navigate("/")
      }
      // console.log('Error updating resource:', error);
    }
    //eslint-disable-next-line
  }, [token])

  const createLibrarylDashboard = useCallback(async (controller, payload, setLoading, setError,) => {
    const allDashboardDetails = await post("addtodashboard", controller, payload, setLoading, setError, token, navigate, dispatch);
    if (allDashboardDetails?.status) {
      return (
        allDashboardDetails.data
      )
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch, token]);

  const bulkShopWithLibrary = useCallback(async (controller, payload, setLoading, setError,) => {
    const bulkShopWithLibrary = await post("updateshops", controller, payload, setLoading, setError, token, navigate, dispatch);
    if (bulkShopWithLibrary?.status) {
      return (
        bulkShopWithLibrary.data
      )
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch, token]);

  const getSources = useCallback(async (controller, setLoading, setError) => {
    const res = await get("sources", controller, setLoading, setError, token, navigate, dispatch);
    if (res?.status) {
      return dispatch(setSourceData(res?.data));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch, token])

  const getGroupsinLib = useCallback(async (controller, id, setLoading, setError) => {
    const res = await get(`getchartdetails?group=${id}`, controller, setLoading, setError, token, navigate, dispatch);
    if (res?.status) {
      return res?.data;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch, token])

  const getGroupsinDashboard = useCallback(async (controller, id, setLoading, setError) => {
    const res = await get(`groupsindashboard?groups=${id}`, controller, setLoading, setError, token, navigate, dispatch);
    if (res?.status) {
      return res?.data;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch, token])

  return {
    fetchMyMarket,
    fetchVisualizedCount,
    fetchExcelData,
    fetchAuthToken,
    deleteMyMarket,
    deleteGroupShop,
    getStaticDatas,
    fetchAirlineData,
    fetchMarketGroup,
    getLogs,
    onHold,
    onResume,
    addMarketGroup,
    editMarketGroup,
    addGroupToDashboard,
    accountData,
    addVizualise,
    fetchTabluaeData,
    fetchVisualizeInfo,
    fetchCloneInfo,
    fetchNotificationInfo,
    passwordChange,
    scorecard,
    getMarketGroup,
    conFirmInGroup,
    groupDataDetails,
    fetchUserDetails,
    globalDownload,
    userAirlineData,
    deleteBulkMyMarket,
    onHoldBulkMyMarket,
    onResumeBulkMyMarket,
    getAllDashboard,
    deleteDashboards,
    addDashboard,
    editDashboard,
    createLibrarylDashboard,
    fetchMyMarketforgroup,
    getSources,
    bulkConFirmInGroup,
    getOverAllTableau,
    getshopsinGroup,
    getGroupsinLib,
    getGroupsinDashboard,
    bulkShopWithLibrary,
    getLegacyDashboard,
    ipLogUpdate,
    loginWithLink,
    shopDownload,
    getOverviewStats
  };
}
export default APIServices
